/*
 * @Author: Ryan
 * @Date: 2026-02-04
 * @Description: Why choose FalcoCut AI Face Swap section with feature cards
 */
import React from 'react';

interface FeatureCard {
  icon: string;
  title: string;
  description: string;
}

const features: FeatureCard[] = [
  {
    icon: '\u26A1\uFE0F',
    title: 'Effortless & Fast',
    description:
      'No editing skills required — just upload your video and get a flawless face swap in seconds. Perfect for beginners and pros alike.',
  },
  {
    icon: '\uD83C\uDFAD',
    title: 'Hyper-Realistic Results',
    description:
      "Our AI ensures natural blending of facial features, lighting, and skin tones. Every swap feels authentic, whether it's a simple selfie or a high-resolution video.",
  },
  {
    icon: '\uD83D\uDD12',
    title: 'Secure & Private',
    description:
      'Your uploads stay safe with encrypted processing and auto-deletion after generation. We never share your data — your content always remains yours.',
  },
  {
    icon: '\uD83E\uDDE9',
    title: 'Versatile for Any Use',
    description:
      'From memes and social posts to e-commerce try-ons and creative film projects, our tool adapts to any scenario. One platform, endless possibilities.',
  },
  {
    icon: '\uD83D\uDC65',
    title: 'Multi-Face Support',
    description:
      'Easily swap multiple faces in one video. Ideal for group videos, team content, and multi-character scenes.',
  },
  {
    icon: '\uD83C\uDF9E\uFE0F',
    title: 'High-Quality Output',
    description:
      'Export face-swapped videos in high resolution, ready for social media, marketing, or professional use.',
  },
];

const FeatureCardItem: React.FC<FeatureCard> = ({ icon, title, description }) => {
  return (
    <div className="bg-white rounded-2xl p-6 pad:p-8 shadow-sm border border-gray-100">
      <div className="text-3xl pad:text-4xl mb-3 pad:mb-4">{icon}</div>
      <h3 className="text-lg pad:text-xl font-semibold text-gray-900 mb-2 pad:mb-3">
        {title}
      </h3>
      <p className="text-sm pad:text-base text-gray-600 leading-relaxed">{description}</p>
    </div>
  );
};

const WhyChooseSection: React.FC = () => {
  return (
    <section className="w-full bg-[#F5F7FB] py-[60px] pad:py-[80px] sm:py-[100px] px-4 pad:px-8 sm:px-[65px]">
      <div className="max-w-[1190px] mx-auto">
        <h2 className="text-2xl pad:text-3xl sm:text-[42px] font-bold text-center text-gray-900 mb-4 pad:mb-5 leading-tight">
          Why Choose FalcoCut AI Face Swap
        </h2>

        <p className="text-sm pad:text-base sm:text-base text-center text-gray-600 max-w-4xl mx-auto mb-10 pad:mb-14 sm:mb-16 leading-relaxed">
          Our AI-powered face swap tool combines speed, realism, and security to make
          content creation easier than ever. Whether you&apos;re experimenting for fun,
          crafting viral social content, or building professional marketing assets, we
          ensure every swap looks seamless and natural.
        </p>

        <div className="grid grid-cols-1 pad:grid-cols-2 gap-4 pad:gap-6">
          {features.map((feature, index) => (
            <FeatureCardItem
              key={index}
              icon={feature.icon}
              title={feature.title}
              description={feature.description}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default WhyChooseSection;
